import { Injectable } from '@angular/core';
import { TxApiService } from './txapi.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Interface for Quick Access Entry
 */
export interface QuickAccessEntry {
	id: string;
	bezeichnung: string; // Display name/label
	auftragsnummer: string; // Job number
	auftragsbezeichnung: string; // Job name for display
	schrittnummer: string; // Work step number
	schrittbezeichnung: string; // Work step name for display
}

/**
 * Interface for Quick Access Settings
 */
export interface QuickAccessSettings {
	title: string; // Custom widget title
	entries: QuickAccessEntry[];
}

/**
 * Service for managing Quick Access (Direktwahl) settings
 * Allows users to save and load quick access buttons for job bookings
 * Uses localStorage for data persistence as fallback
 */
@Injectable({
	providedIn: 'root',
})
export class QuickAccessService {
	private readonly SETTING_NAME = 'TXP_QUICK_ACCESS';
	private readonly LOCALSTORAGE_KEY = 'txp_quick_access_entries';
	private quickAccessEntries$ = new BehaviorSubject<QuickAccessEntry[]>([]);
	private quickAccessTitle$ = new BehaviorSubject<string>('');
	private isInitialized = false;

	constructor(private txApiService: TxApiService) {
		// Don't load immediately - wait for explicit loadQuickAccessEntries() call
		// This prevents localStorage-only initialization before server data is fetched
	}

	/**
	 * Load quick access entries from server or localStorage
	 */
	loadQuickAccessEntries(): Observable<QuickAccessEntry[]> {
		// If already initialized, just return current value
		if (this.isInitialized && this.quickAccessEntries$.getValue().length > 0) {
			return of(this.quickAccessEntries$.getValue());
		}

		// Try to load from server first
		return this.txApiService.callAPI('getSetting', { settingName: this.SETTING_NAME }).pipe(
			map((response) => {
				this.isInitialized = true;
				
				if (response.statuscode === 0) {
					// Check both settingString and settingValue for backwards compatibility
					const settingsData = response.settingString || response.settingValue;
					
				if (settingsData) {
					try {
						// Parse the settings string
						const parsed = JSON.parse(settingsData);
						
						// Check if it's the new format (object with title and entries) or old format (array)
						let entries: QuickAccessEntry[];
						let title: string;
						
						if (Array.isArray(parsed)) {
							// Old format: just an array of entries
							entries = parsed;
							title = '';
						} else {
							// New format: object with title and entries
							entries = parsed.entries || [];
							title = parsed.title || '';
						}
						
						this.quickAccessEntries$.next(entries);
						this.quickAccessTitle$.next(title);
						// Save in new format to localStorage
						const settings: QuickAccessSettings = { title, entries };
						localStorage.setItem(this.LOCALSTORAGE_KEY, JSON.stringify(settings));
						console.log('Quick access entries loaded from server:', entries.length);
						return entries;
						} catch (error) {
							console.error('Error parsing quick access settings from server:', error);
							// Fall back to localStorage
							return this.loadFromLocalStorage();
						}
					} else {
						// No settings found on server, try localStorage
						console.log('No quick access settings on server, checking localStorage');
						return this.loadFromLocalStorage();
					}
				} else {
					// Error response, try localStorage
					console.warn('Server error loading quick access, trying localStorage');
					return this.loadFromLocalStorage();
				}
			}),
			catchError((error) => {
				console.error('Error loading quick access settings from server:', error);
				this.isInitialized = true;
				// Fall back to localStorage
				return of(this.loadFromLocalStorage());
			})
		);
	}

	/**
	 * Load from localStorage
	 */
	private loadFromLocalStorage(): QuickAccessEntry[] {
		try {
			const stored = localStorage.getItem(this.LOCALSTORAGE_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);
				
				// Check if it's the new format (object with title and entries) or old format (array)
				let entries: QuickAccessEntry[];
				let title: string;
				
				if (Array.isArray(parsed)) {
					// Old format: just an array of entries
					entries = parsed;
					title = '';
				} else {
					// New format: object with title and entries
					entries = parsed.entries || [];
					title = parsed.title || '';
				}
				
				this.quickAccessEntries$.next(entries);
				this.quickAccessTitle$.next(title);
				console.log('Quick access entries loaded from localStorage:', entries.length);
				return entries;
			}
		} catch (error) {
			console.error('Error loading from localStorage:', error);
		}
		console.log('No quick access entries found in localStorage');
		this.quickAccessEntries$.next([]);
		return [];
	}

	/**
	 * Save quick access entries to server and localStorage
	 */
	saveQuickAccessEntries(entries: QuickAccessEntry[]): Observable<any> {
		const settings: QuickAccessSettings = {
			title: this.quickAccessTitle$.getValue(),
			entries: entries
		};
		const settingsString = JSON.stringify(settings);
		
		// Always save to localStorage immediately
		try {
			localStorage.setItem(this.LOCALSTORAGE_KEY, settingsString);
			this.quickAccessEntries$.next(entries);
		} catch (error) {
			console.error('Error saving to localStorage:', error);
		}

		// Try to save to server (optional, won't fail if server endpoint doesn't work)
		return this.txApiService.callAPI('updateUserSettings', {
			settingName: this.SETTING_NAME,
			settingValue: null,
			settingsString: settingsString,
			settingGlobal: true,
		}).pipe(
			map((response) => {
				if (response.statuscode === 0) {
					console.log('Quick access settings saved to server successfully');
				} else {
					console.warn('Server save failed, but data is saved locally');
				}
				return response;
			}),
			catchError((error) => {
				console.warn('Error saving to server (data is still saved locally):', error);
				// Return success anyway since we have localStorage
				return of({ statuscode: 0, statustext: 'Saved locally' });
			})
		);
	}

	/**
	 * Get current quick access entries as observable
	 */
	getQuickAccessEntries(): Observable<QuickAccessEntry[]> {
		return this.quickAccessEntries$.asObservable();
	}

	/**
	 * Get current quick access entries value
	 */
	getQuickAccessEntriesValue(): QuickAccessEntry[] {
		return this.quickAccessEntries$.getValue();
	}

	/**
	 * Add a new quick access entry
	 */
	addQuickAccessEntry(entry: QuickAccessEntry): Observable<any> {
		const currentEntries = this.quickAccessEntries$.getValue();
		const newEntries = [...currentEntries, entry];
		return this.saveQuickAccessEntries(newEntries);
	}

	/**
	 * Update an existing quick access entry
	 */
	updateQuickAccessEntry(id: string, updatedEntry: QuickAccessEntry): Observable<any> {
		const currentEntries = this.quickAccessEntries$.getValue();
		const index = currentEntries.findIndex((entry) => entry.id === id);
		if (index !== -1) {
			const newEntries = [...currentEntries];
			newEntries[index] = updatedEntry;
			return this.saveQuickAccessEntries(newEntries);
		}
		return of({ statuscode: -1, statustext: 'Entry not found' });
	}

	/**
	 * Delete a quick access entry
	 */
	deleteQuickAccessEntry(id: string): Observable<any> {
		const currentEntries = this.quickAccessEntries$.getValue();
		const newEntries = currentEntries.filter((entry) => entry.id !== id);
		return this.saveQuickAccessEntries(newEntries);
	}

	/**
	 * Generate unique ID for new entry
	 */
	generateId(): string {
		return `qa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	}

	/**
	 * Get quick access title as observable
	 */
	getQuickAccessTitle(): Observable<string> {
		return this.quickAccessTitle$.asObservable();
	}

	/**
	 * Update quick access title
	 */
	updateQuickAccessTitle(title: string): Observable<any> {
		this.quickAccessTitle$.next(title);
		const currentEntries = this.quickAccessEntries$.getValue();
		return this.saveQuickAccessEntries(currentEntries);
	}
}
